// src/pages/BuyNow/BuyNow.jsx
import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Box,
  IconButton,
  Chip,
  Paper,
  Typography,
  TablePagination,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from "@mui/material";

import {
  Download,
  Visibility,
  Close,
  Payment,
  Receipt,
} from "@mui/icons-material";
import {
  getVehicleOrders,
  updateOrderStatus,
  getAdvanceEvoPayments,
} from "../../features/auth/service";
import moment from "moment";
import "./BuyNow.css";

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="buynow-wrapper">
          <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              We're sorry, but an error occurred while loading this page.
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </Paper>
        </div>
      );
    }
    return this.props.children;
  }
}

const STATUS_TRANSITIONS = {
  CONFIRMED: ["DISPATCHED"],
  DISPATCHED: ["DELIVERED", "CANCELLED"],
  DELIVERED: [],
  CANCELLED: [],
};

const STATUS_CONFIG = {
  CONFIRMED: {
    label: "Confirmed",
    color: "#1976d2", // blue
  },
  DISPATCHED: {
    label: "Dispatched",
    color: "#ed6c02", // orange
  },
  DELIVERED: {
    label: "Delivered",
    color: "#2e7d32", // green
  },
  CANCELLED: {
    label: "Cancelled",
    color: "#d32f2f", // red
  },
};

const BuyNow = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [componentError, setComponentError] = useState(null);
  const [showAdvanceBookings, setShowAdvanceBookings] = useState(false); // false = Vehicle Orders, true = Advance Bookings

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusChange, setStatusChange] = useState({
    open: false,
    orderId: null,
    newStatus: null,
  });

  // Filter states
  const [filters, setFilters] = useState({
    status: "",
    search: "",
  });

  // Order details modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  console.log(selectedOrder, "selectedOrder");
  // Snackbar for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Helper function to get payment ID from payments array
  const getPaymentId = (order) => {
    try {
      // Access through transactionDetails -> paymentDetails -> data -> payments array
      const payments =
        order?.transactionDetails?.paymentDetails?.data?.payments;
      if (payments && payments.length > 0 && payments[0]?.id) {
        return payments[0].id; // This will return "v1-260303063318-aa-wMirYg-up-a" or similar
      }
      // Fallback to existing transactionId if payment ID not found
      return order?.pineLabOrderId || "N/A";
    } catch {
      return order?.pineLabOrderId || "N/A";
    }
  };

  // Helper function to extract meaningful error message from API response
  const getErrorMessage = (error) => {
    if (!error) return "Something went wrong";

    // If error is a string, return it
    if (typeof error === "string") return error;

    // If error has a message property that's a string
    if (error.message && typeof error.message === "string") {
      // Check if message is an empty object string
      if (error.message === "{}" || error.message === "[object Object]") {
        return "Something went wrong";
      }
      return error.message;
    }

    // If error has response data with message
    if (error.response?.data?.message) {
      const msg = error.response.data.message;
      if (typeof msg === "object") {
        if (msg.message) return msg.message;
        if (msg.code) {
          const codeMessages = {
            AMOUNT_LIMIT_EXCEEDED:
              "Requested refund amount is greater than eligible amount",
          };
          return codeMessages[msg.code] || `Refund failed: ${msg.code}`;
        }
      }
      if (msg === "{}" || msg === "[object Object]") {
        return "Something went wrong";
      }
      return msg;
    }

    // If error has an error property with message
    if (error.error && typeof error.error === "object") {
      if (error.error.message) {
        return error.error.message;
      }
    }

    return "Something went wrong";
  };

  // Safe string conversion helper
  const safeString = (value, defaultValue = "N/A") => {
    if (value === null || value === undefined) return defaultValue;
    if (typeof value === "object") {
      // Don't convert objects to string directly
      return defaultValue;
    }
    return String(value);
  };

  // Safe number conversion helper
  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined) return defaultValue;
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Safe object access helper
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      const keys = path.split(".");
      let result = obj;
      for (const key of keys) {
        if (result === null || result === undefined) return defaultValue;
        result = result[key];
      }
      return result !== undefined && result !== null ? result : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Fetch orders based on active view
  const fetchOrders = async () => {
    setLoading(true);
    setComponentError(null);
    try {
      console.log("Fetching orders with params:", {
        page: page + 1,
        pageSize: rowsPerPage,
        search: filters.search,
        status: filters.status,
        showAdvanceBookings: showAdvanceBookings,
      });

      let res;
      if (!showAdvanceBookings) {
        res = await getVehicleOrders({
          page: page + 1,
          pageSize: rowsPerPage,
          search: filters.search || null,
          status: filters.status || null,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
      } else {
        res = await getAdvanceEvoPayments({
          page: page + 1,
          pageSize: rowsPerPage,
          search: filters.search || null,
          status: filters.status || null,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
      }

      console.log("API Response:", res);

      // Validate response structure (same for both endpoints)
      if (res && res.user && Array.isArray(res.user.data)) {
        // Validate each order object
        const validOrders = res.user.data.filter(
          (order) =>
            order && typeof order === "object" && !Array.isArray(order),
        );
        console.log(res.user.data, "validOrders");
        setOrders(validOrders);
        setTotalCount(safeNumber(res.user.totalCount, validOrders.length));
        setTotalAmount(safeNumber(res.user.totalAmount, 0));
        setTotalPages(safeNumber(res.user.totalPages, 1));
      } else {
        console.warn("Invalid response structure:", res);
        setOrders([]);
        setTotalCount(0);
        setTotalAmount(0);
        setTotalPages(1);

        // Show appropriate error message
        if (!res) {
          showNotification("No response from server", "error");
        } else if (!res.user) {
          showNotification("Invalid response format", "error");
        } else {
          showNotification("No orders found", "info");
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
      setTotalCount(0);
      setTotalPages(1);
      setComponentError(error);

      // Extract and show meaningful error message
      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [page, rowsPerPage, filters.status, filters.search, showAdvanceBookings]);

  // Handle showing advance bookings
  const handleShowAdvanceBookings = () => {
    setShowAdvanceBookings(true);
    setPage(0);
    setFilters({ status: "", search: "" }); // Reset filters when switching
  };

  // Handle showing vehicle orders
  const handleShowVehicleOrders = () => {
    setShowAdvanceBookings(false);
    setPage(0);
    setFilters({ status: "", search: "" }); // Reset filters when switching
  };

  // Handle filter changes
  const handleStatusFilter = (event) => {
    console.log(event.target.value, "evnet");
    try {
      setFilters({ ...filters, status: event.target.value });
      setPage(0);
    } catch (error) {
      console.error("Error in status filter:", error);
      showNotification("Error applying filter", "error");
    }
  };

  const handleSearchChange = (event) => {
    try {
      setFilters({ ...filters, search: event.target.value });
      setPage(0);
    } catch (error) {
      console.error("Error in search:", error);
      showNotification("Error applying search", "error");
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    try {
      console.log("Changing page to:", newPage);
      setPage(newPage);
    } catch (error) {
      console.error("Error changing page:", error);
      showNotification("Error changing page", "error");
    }
  };

  const handleChangeRowsPerPage = (event) => {
    try {
      const newRowsPerPage = parseInt(event.target.value, 10);
      console.log("Changing rows per page to:", newRowsPerPage);
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    } catch (error) {
      console.error("Error changing rows per page:", error);
      showNotification("Error changing rows per page", "error");
    }
  };

  // Handle view order details with validation
  const handleViewOrder = (order) => {
    try {
      if (!order || typeof order !== "object") {
        showNotification("Invalid order data", "error");
        return;
      }
      setSelectedOrder(order);
      setShowOrderDetails(true);
    } catch (error) {
      console.error("Error viewing order:", error);
      showNotification("Error viewing order details", "error");
    }
  };

  // Handle invoice download
  const handleDownloadInvoice = (invoiceUrl, orderId) => {
    try {
      if (invoiceUrl && typeof invoiceUrl === "string" && invoiceUrl.trim()) {
        window.open(invoiceUrl, "_blank", "noopener,noreferrer");
      } else {
        showNotification("Invoice not available", "warning");
      }
    } catch (error) {
      console.error("Error downloading invoice:", error);
      showNotification("Error downloading invoice", "error");
    }
  };

  // Show notification with proper error message
  const showNotification = (message, severity = "success") => {
    try {
      // Ensure message is a string and not an empty object representation
      let displayMessage = "Something went wrong";

      if (typeof message === "string") {
        // Check if the string is not just an empty object representation
        if (message && message !== "{}" && message !== "[object Object]") {
          displayMessage = message;
        }
      } else if (message && typeof message === "object") {
        // Try to extract message from object
        const extractedMessage = getErrorMessage(message);
        if (
          extractedMessage &&
          extractedMessage !== "{}" &&
          extractedMessage !== "[object Object]"
        ) {
          displayMessage = extractedMessage;
        }
      }

      setSnackbar({
        open: true,
        message: displayMessage,
        severity: severity,
      });
    } catch (error) {
      console.error("Error showing notification:", error);
      // Fallback notification
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  };

  // Format currency safely
  const formatCurrency = (amount) => {
    try {
      const num = safeNumber(amount);
      return `₹${num.toLocaleString("en-IN")}`;
    } catch {
      return "₹0";
    }
  };

  // Format date safely
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      return moment(dateString).format("DD-MMM-YYYY HH:mm");
    } catch {
      return "Invalid Date";
    }
  };

  // Handle export data
  const handleExportData = async () => {
    setExportLoading(true);
    try {
      let res;
      if (!showAdvanceBookings) {
        res = await getVehicleOrders({
          page: 1,
          pageSize: 10000,
          search: filters.search || null,
          status: filters.status || null,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
      } else {
        res = await getAdvanceEvoPayments({
          page: 1,
          pageSize: 10000,
          search: filters.search || null,
          status: filters.status || null,
          sortBy: "createdAt",
          sortOrder: "desc",
        });
      }

      if (res?.user?.data && Array.isArray(res.user.data)) {
        const exportData = res.user.data.map((order) => ({
          "Order Reference": safeGet(order, "merchantOrderReference", "N/A"),
          "Transaction ID": getPaymentId(order),
          "Customer Name": safeGet(order, "customerDetails.name", "N/A"),
          Email: safeGet(order, "customerDetails.email", "N/A"),
          Mobile: safeGet(order, "customerDetails.phone", "N/A"),
          Product: safeGet(order, "productDetails.productName", "N/A"),
          Amount: formatCurrency(order.amount),
          Status: safeString(order.status),
          "Order Date": formatDate(order.createdAt),
          "Payment Method": safeGet(
            order,
            "transactionDetails.paymentMethod",
            "N/A",
          ),
          "Payment Status": safeGet(
            order,
            "transactionDetails.paymentStatus",
            "N/A",
          ),
          City: safeGet(order, "shippingAddress.city", "N/A"),
          State: safeGet(order, "shippingAddress.state", "N/A"),
          "Showroom Name": safeGet(order, "shippingAddress.name", "N/A"),
          RRN: safeGet(order, "transactionDetails.rrn", "N/A"),
          ARN: safeGet(order, "transactionDetails.arn", "N/A"),
          "Product Code": safeGet(order, "productDetails.productCode", "N/A"),
          Model: safeGet(order, "productDetails.model", "N/A"),
          Color: safeGet(order, "productDetails.color", "N/A"),
          Variant: safeGet(order, "productDetails.variant", "N/A"),
        }));

        const XLSX = await import("xlsx");
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          worksheet,
          !showAdvanceBookings ? "Vehicle Orders" : "Advance Bookings",
        );

        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
        });

        const { default: saveAs } = await import("file-saver");
        saveAs(
          blob,
          `${!showAdvanceBookings ? "BuyNow_Orders" : "Advance_Bookings"}_${moment().format("DD-MMM-YYYY_HHmm")}.xlsx`,
        );

        showNotification("Export completed successfully", "success");
      } else {
        showNotification("No data to export", "warning");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      const errorMessage = getErrorMessage(error);
      showNotification(errorMessage, "error");
    } finally {
      setExportLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const payload = {
      orderId: orderId,
      status: newStatus,
    };
    try {
      console.log("Updating status:", orderId, newStatus);

      await updateOrderStatus(payload);

      showNotification("Status updated successfully", "success");

      fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      showNotification("Failed to update status", "error");
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!statusChange.orderId || !statusChange.newStatus) return;

    try {
      await updateOrderStatus({
        orderId: statusChange.orderId,
        status: statusChange.newStatus,
      });
      showNotification("Status updated successfully", "success");
      fetchOrders();
    } catch (error) {
      console.error("Error updating status:", error);
      showNotification("Failed to update status", "error");
    } finally {
      setStatusChange({ open: false, orderId: null, newStatus: null });
    }
  };

  const handleCancelStatusChange = () => {
    setStatusChange({ open: false, orderId: null, newStatus: null });
  };

  // If component error, show error UI
  if (componentError) {
    return (
      <div className="buynow-wrapper">
        <Paper sx={{ p: 4, textAlign: "center", m: 2 }}>
          <Typography variant="h5" color="error" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            {getErrorMessage(componentError)}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="contained"
              onClick={() => {
                setComponentError(null);
                fetchOrders();
              }}
            >
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </Box>
        </Paper>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="buynow-wrapper">
        <div className="buynow-header-container">
          <span className="buynow-header">
            {!showAdvanceBookings ? "Buy Now Orders" : "Advance Bookings"}
          </span>
          <div className="btn-container">
            {exportLoading ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                onClick={handleExportData}
                variant="contained"
                className="bordered-button"
                startIcon={<Download />}
                disabled={orders.length === 0}
              >
                Export Data
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <Paper elevation={0} className="stat-card">
            <Typography variant="h6" color="textSecondary">
              Total {!showAdvanceBookings ? "Orders" : "Bookings"}
            </Typography>
            <Typography variant="h4">{totalCount}</Typography>
          </Paper>
          <Paper elevation={0} className="stat-card">
            <Typography variant="h6" color="textSecondary">
              Total Amount
            </Typography>
            <Typography variant="h4">{formatCurrency(totalAmount)}</Typography>
          </Paper>
        </div>

        {/* Advance Bookings Button and Filters */}
        <div
          className="filters-container"
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <FormControl sx={{ minWidth: 250 }} size="small">
            <TextField
              label="Search by customer name, email or mobile"
              variant="outlined"
              value={filters.search}
              onChange={handleSearchChange}
              size="small"
            />
          </FormControl>
          <FormControl sx={{ minWidth: 180 }} size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={handleStatusFilter}
            >
              <MenuItem value="">All</MenuItem>
              {Object.keys(STATUS_CONFIG).map((statusKey) => (
                <MenuItem key={statusKey} value={statusKey}>
                  {STATUS_CONFIG[statusKey].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!showAdvanceBookings ? (
            <Button
              variant="outlined"
              onClick={handleShowAdvanceBookings}
              color="secondary"
            >
              View Advance Payments
            </Button>
          ) : (
            <Button
              variant="outlined"
              onClick={handleShowVehicleOrders}
              color="primary"
            >
              View All Vehicle Orders
            </Button>
          )}
        </div>

        {/* Orders Table */}
        <div className="table-container">
          {loading ? (
            <div className="loading-container">
              <CircularProgress />
              <Typography variant="body1" sx={{ mt: 2 }}>
                Loading {!showAdvanceBookings ? "orders" : "bookings"}...
              </Typography>
            </div>
          ) : (
            <>
              <table className="orders-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>City</th>
                    <th>Showroom</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Order Date</th>
                    <th>Invoice</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">
                        <Typography variant="body1">
                          No {!showAdvanceBookings ? "orders" : "bookings"}{" "}
                          found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Try adjusting your search or filters
                        </Typography>
                      </td>
                    </tr>
                  ) : (
                    orders.map((order) => {
                      try {
                        if (!order || typeof order !== "object") return null;

                        return (
                          <tr key={order._id || Math.random()}>
                            <td>
                              {safeGet(order, "customerDetails.name", "N/A")}
                            </td>
                            <td>
                              {safeGet(order, "customerDetails.email", "N/A")}
                            </td>
                            <td>
                              {safeGet(order, "customerDetails.phone", "N/A")}
                            </td>
                            <td>
                              {safeGet(order, "shippingAddress.city", "N/A")}
                            </td>
                            <td>
                              {safeGet(order, "shippingAddress.name", "N/A")}
                            </td>
                            <td>
                              {safeGet(
                                order,
                                "productDetails.productName",
                                "N/A",
                              )}
                            </td>
                            <td>{formatCurrency(order.amount)}</td>
                            <td>
                              {order.status === "DELIVERED" ||
                              order.status === "CANCELLED" ? (
                                <Chip
                                  label={
                                    STATUS_CONFIG[order.status]?.label ||
                                    order.status
                                  }
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      STATUS_CONFIG[order.status]?.color ||
                                      "#1976d2",
                                    color: "#fff",
                                  }}
                                />
                              ) : (
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={order.status || ""}
                                    onChange={(e) =>
                                      setStatusChange({
                                        open: true,
                                        orderId: order._id,
                                        newStatus: e.target.value,
                                      })
                                    }
                                  >
                                    <MenuItem value={order.status}>
                                      {STATUS_CONFIG[order.status]?.label}
                                    </MenuItem>

                                    {(
                                      STATUS_TRANSITIONS[order.status] || []
                                    ).map((statusKey) => (
                                      <MenuItem
                                        key={statusKey}
                                        value={statusKey}
                                      >
                                        {STATUS_CONFIG[statusKey].label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              )}
                            </td>
                            <td>{formatDate(order.createdAt)}</td>
                            <td>
                              {order.memoUrl ? (
                                <IconButton
                                  onClick={() =>
                                    handleDownloadInvoice(
                                      order.memoUrl,
                                      order._id,
                                    )
                                  }
                                  color="primary"
                                  size="small"
                                  title="Download Invoice"
                                >
                                  <Download />
                                </IconButton>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  No invoice
                                </Typography>
                              )}
                            </td>
                            <td>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  alignItems: "center",
                                }}
                              >
                                <IconButton
                                  onClick={() => handleViewOrder(order)}
                                  color="primary"
                                  size="small"
                                  title="View Details"
                                >
                                  <Visibility />
                                </IconButton>
                              </Box>
                            </td>
                          </tr>
                        );
                      } catch (error) {
                        console.error("Error rendering order row:", error);
                        return null;
                      }
                    })
                  )}
                </tbody>
              </table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                className="pagination"
                showFirstButton
                showLastButton
              />
            </>
          )}
        </div>

        {/* Order Details Modal */}
        <Modal
          open={showOrderDetails}
          onClose={() => setShowOrderDetails(false)}
          aria-labelledby="order-details-modal"
        >
          <Paper className="order-details-modal">
            <div className="modal-header">
              <Typography variant="h5">Order Details</Typography>
              <IconButton onClick={() => setShowOrderDetails(false)}>
                <Close />
              </IconButton>
            </div>

            {selectedOrder && (
              <div className="modal-content">
                {/* Customer Information */}
                <div className="details-section">
                  <Typography variant="h6" gutterBottom>
                    Customer Information
                  </Typography>
                  <div className="details-grid">
                    <div>
                      <strong>Name:</strong>{" "}
                      {safeGet(selectedOrder, "customerDetails.name", "N/A")}
                    </div>
                    <div>
                      <strong>Email:</strong>{" "}
                      {safeGet(selectedOrder, "customerDetails.email", "N/A")}
                    </div>
                    <div>
                      <strong>Mobile:</strong>{" "}
                      {safeGet(selectedOrder, "customerDetails.phone", "N/A")}
                    </div>
                    {safeGet(selectedOrder, "customerDetails.whatsapp") && (
                      <div>
                        <strong>WhatsApp:</strong>{" "}
                        {safeGet(selectedOrder, "customerDetails.whatsapp")}
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Information */}
                <div className="details-section">
                  <Typography variant="h6" gutterBottom>
                    Order Information
                  </Typography>
                  <div className="details-grid">
                    <div>
                      <strong>Order Reference:</strong>{" "}
                      {safeGet(selectedOrder, "merchantOrderReference", "N/A")}
                    </div>
                    <div>
                      <strong>Transaction ID:</strong>{" "}
                      {getPaymentId(selectedOrder)}
                    </div>
                    <div>
                      <strong>Product:</strong>{" "}
                      {safeGet(
                        selectedOrder,
                        "productDetails.productName",
                        "N/A",
                      )}
                    </div>
                    <div>
                      <strong>Amount:</strong>{" "}
                      {formatCurrency(selectedOrder.amount)}
                    </div>
                    <div>
                      <strong>Status:</strong>{" "}
                      <Chip
                        label={
                          STATUS_CONFIG[selectedOrder.status]?.label ||
                          selectedOrder.status
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            STATUS_CONFIG[selectedOrder.status]?.color ||
                            "#1976d2",
                          color: "#fff",
                        }}
                      />
                    </div>
                    <div>
                      <strong>Status Timeline:</strong>
                      {[
                        "CONFIRMED",
                        "DISPATCHED",
                        "DELIVERED",
                        "CANCELLED",
                      ].map((statusKey) => {
                        const statusLabel =
                          STATUS_CONFIG[statusKey]?.label || statusKey;
                        const dateField = {
                          CONFIRMED: selectedOrder.createdAt,
                          DISPATCHED: selectedOrder.dispatchedAt,
                          DELIVERED: selectedOrder.deliveredAt,
                          CANCELLED: selectedOrder.cancelledAt,
                        }[statusKey];

                        if (!dateField) return null;

                        return (
                          <Typography
                            key={statusKey}
                            variant="caption"
                            display="block"
                          >
                            {statusLabel}: {formatDate(dateField)}
                          </Typography>
                        );
                      })}
                    </div>
                    <div>
                      <strong>Order Date:</strong>{" "}
                      {formatDate(selectedOrder.createdAt)}
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                {selectedOrder.productDetails && (
                  <div className="details-section">
                    <Typography variant="h6" gutterBottom>
                      Product Details
                    </Typography>
                    <div className="details-grid">
                      <div>
                        <strong>Product Code:</strong>{" "}
                        {safeGet(
                          selectedOrder,
                          "productDetails.productCode",
                          "N/A",
                        )}
                      </div>
                      <div>
                        <strong>Model:</strong>{" "}
                        {safeGet(selectedOrder, "productDetails.model", "N/A")}
                      </div>
                      <div>
                        <strong>Color:</strong>{" "}
                        {safeGet(selectedOrder, "productDetails.color", "N/A")}
                      </div>
                      <div>
                        <strong>Variant:</strong>{" "}
                        {safeGet(
                          selectedOrder,
                          "productDetails.variant",
                          "N/A",
                        )}
                      </div>
                      {safeGet(selectedOrder, "productDetails.description") && (
                        <div>
                          <strong>Description:</strong>{" "}
                          {safeGet(selectedOrder, "productDetails.description")}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Shipping Address */}
                {selectedOrder.shippingAddress && (
                  <div className="details-section">
                    <Typography variant="h6" gutterBottom>
                      Shipping Address
                    </Typography>
                    <div className="details-grid">
                      <div>
                        <strong>Address:</strong>{" "}
                        {safeGet(
                          selectedOrder,
                          "shippingAddress.address",
                          "N/A",
                        )}
                      </div>
                      <div>
                        <strong>City:</strong>{" "}
                        {safeGet(selectedOrder, "shippingAddress.city", "N/A")}
                      </div>
                      <div>
                        <strong>State:</strong>{" "}
                        {safeGet(selectedOrder, "shippingAddress.state", "N/A")}
                      </div>
                      <div>
                        <strong>Showroom Name:</strong>{" "}
                        {safeGet(selectedOrder, "shippingAddress.name", "N/A")}
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Details */}
                {selectedOrder.transactionDetails && (
                  <div className="details-section">
                    <Typography variant="h6" gutterBottom>
                      Payment Details
                    </Typography>
                    <div className="details-grid">
                      <div>
                        <strong>Payment Method:</strong>{" "}
                        {safeGet(
                          selectedOrder,
                          "transactionDetails.paymentMethod",
                          "N/A",
                        )}
                      </div>
                      <div>
                        <strong>Payment Status:</strong>{" "}
                        {safeGet(
                          selectedOrder,
                          "transactionDetails.paymentStatus",
                          "N/A",
                        )}
                      </div>
                      <div>
                        <strong>Transaction Date:</strong>{" "}
                        {selectedOrder.transactionDetails?.transactionCreatedAt
                          ? formatDate(
                              selectedOrder.transactionDetails
                                .transactionCreatedAt,
                            )
                          : "N/A"}
                      </div>

                      {selectedOrder.transactionDetails?.rrn ? (
                        <div>
                          <strong>RRN:</strong>{" "}
                          {selectedOrder.transactionDetails.rrn}
                        </div>
                      ) : selectedOrder.transactionDetails?.arn ? (
                        <div>
                          <strong>ARN:</strong>{" "}
                          {selectedOrder.transactionDetails.arn}
                        </div>
                      ) : null}
                    </div>
                  </div>
                )}

                {/* Invoice Download */}
                {selectedOrder.memoUrl && (
                  <div className="details-section">
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() =>
                        handleDownloadInvoice(
                          selectedOrder.memoUrl,
                          selectedOrder._id,
                        )
                      }
                    >
                      Download Invoice
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Paper>
        </Modal>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
      <Dialog open={statusChange.open} onClose={handleCancelStatusChange}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to change the order status to{" "}
            <span
              style={{
                fontWeight: "bold",
                color:
                  STATUS_CONFIG[statusChange.newStatus]?.color || "inherit",
              }}
            >
              {STATUS_CONFIG[statusChange.newStatus]?.label ||
                statusChange.newStatus}
            </span>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelStatusChange}>Cancel</Button>
          <Button
            onClick={handleConfirmStatusChange}
            variant="contained"
            sx={{
              color: "#fff",
              backgroundColor:
                STATUS_CONFIG[statusChange.newStatus]?.color || "#1976d2",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  );
};

export default BuyNow;
